import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  AlertTriangle, CalendarClock, CheckCircle2, ClipboardList, ExternalLink,
  Loader2, Plus, RefreshCw, Search, SlidersHorizontal, X,
} from "lucide-react";
import api, { formatError } from "@/lib/api";
import { SALESMEN } from "./utils";

const PREP_STATUSES = ["Not Started", "In Progress", "Ready", "Collected"];
const PREP_CLS = {
  "Not Started": "bg-[#F1ECE4] text-[#6B5B4A] border-[#D9CDB8]",
  "In Progress": "bg-[#FBEFD5] text-[#8A6210] border-[#E3C98B]",
  Ready: "bg-[#D6E9D5] text-[#2D4A22] border-[#2D4A22]/25",
  Collected: "bg-[#E5E7EB] text-[#374151] border-[#9CA3AF]/40",
};
const SORTS = [
  ["invoice_desc", "Invoice ↓ (newest first)"],
  ["invoice_asc", "Invoice ↑"],
  ["date_asc", "Expected date ↑"],
  ["date_desc", "Expected date ↓"],
];
const EMPTY_FILTERS = { group: "all", prep: "all", sub: "all", salesman: "all", customer: "", dateFrom: "", dateTo: "", invFrom: "", invTo: "", flag: "" };
const fmtDate = (d) => {
  if (!d) return "—";
  const [y, m, day] = d.split("-");
  return `${day}/${m}/${y}`;
};

const Field = ({ label, children }) => (
  <div><label className="overline text-[10px]">{label}</label>{children}</div>
);

const Modal = ({ title, overline, onClose, children, testId }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4" onClick={onClose} data-testid={testId}>
    <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-5 sm:p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
      <div className="flex items-start justify-between gap-3">
        <div><p className="overline text-[10px]">{overline}</p><h3 className="font-serif text-xl text-ink mt-1">{title}</h3></div>
        <button type="button" onClick={onClose} className="text-muted2 hover:text-maroon" data-testid={`${testId}-close`}><X size={18} /></button>
      </div>
      {children}
    </div>
  </div>
);

const SalesmanSelect = ({ value, onChange, testId }) => (
  <select data-testid={testId} className="luxury-input mt-1 w-full" value={value} onChange={(e) => onChange(e.target.value)}>
    <option value="">Not Assigned</option>
    {SALESMEN.map((s) => <option key={s} value={s}>{s}</option>)}
  </select>
);

function AddInvoiceModal({ fixedGroup, preset, onClose, onSaved }) {
  const [form, setForm] = useState({ invoice_number: preset?.invoice_number || "", group: preset?.group || fixedGroup || "A", customer_name: "", expected_date: "", salesman: "", note: "" });
  const [busy, setBusy] = useState(false);
  const [lookup, setLookup] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: typeof e === "string" ? e : e.target.value }));

  useEffect(() => {
    const inv = form.invoice_number.trim();
    if (!/^[12]\d{3}$/.test(inv)) { setLookup(null); return; }
    let live = true;
    api.get(`/admin/register/lookup/${inv}`).then(({ data }) => live && setLookup(data)).catch(() => live && setLookup(null));
    return () => { live = false; };
  }, [form.invoice_number]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admin/register", form);
      toast.success(`Invoice ${data.invoice_number} added to the ${data.group_label} register${data.form_submitted ? " and linked to its website submission" : ""}.`);
      onSaved(data);
    } catch (err) { toast.error(formatError(err)); } finally { setBusy(false); }
  };

  return (
    <Modal title="Add Invoice" overline="Preparation Register" onClose={onClose} testId="register-add-modal">
      <form onSubmit={submit} className="mt-4 space-y-3">
        <Field label="Invoice Number *">
          <input data-testid="register-invoice-input" className="luxury-input mt-1" inputMode="numeric" maxLength={4} value={form.invoice_number} onChange={set("invoice_number")} placeholder={form.group === "B" ? "2000–2999" : "1000–1999"} required />
        </Field>
        <Field label="Group *">
          <div className="mt-1 flex gap-2" data-testid="register-group-toggle">
            {[["A", "Sider"], ["B", "Bridal"]].map(([g, l]) => (
              <button key={g} type="button" disabled={!!fixedGroup && fixedGroup !== g} onClick={() => setForm((f) => ({ ...f, group: g }))}
                data-testid={`register-group-${g}`}
                className={`flex-1 rounded-full px-3 py-2 text-xs uppercase tracking-[0.18em] border transition-colors disabled:opacity-40 ${form.group === g ? "bg-maroon text-ivory border-maroon" : "bg-white text-muted2 border-line"}`}>{l}</button>
            ))}
          </div>
        </Field>
        {lookup && (
          <div data-testid="register-lookup-hint" className={`rounded-xl border px-3 py-2 text-xs ${lookup.entry ? "border-[#E3C98B] bg-[#FBEFD5] text-[#8A6210]" : lookup.form_submitted ? "border-[#D6E9D5] bg-[#EDF6EA] text-[#2D4A22]" : "border-line bg-[#FAF7F2] text-muted2"}`}>
            {lookup.entry ? "This invoice is already in the register." : lookup.form_submitted ? "Website form already submitted — the entry will be linked to it." : "No website submission yet for this invoice."}
          </div>
        )}
        <Field label="Customer Name"><input data-testid="register-customer-input" className="luxury-input mt-1" value={form.customer_name} onChange={set("customer_name")} maxLength={120} /></Field>
        <Field label="Expected Collection Date"><input data-testid="register-date-input" type="date" className="luxury-input mt-1" value={form.expected_date} onChange={set("expected_date")} /></Field>
        <Field label="Assigned Salesman"><SalesmanSelect value={form.salesman} onChange={set("salesman")} testId="register-salesman-select" /></Field>
        <Field label="Preparation Note"><textarea data-testid="register-note-input" className="luxury-input mt-1 min-h-[70px]" value={form.note} onChange={set("note")} maxLength={500} /></Field>
        <div className="flex gap-2 pt-1">
          <button type="button" onClick={onClose} className="btn-outline-maroon flex-1 !py-2 !text-xs">Cancel</button>
          <button type="submit" disabled={busy || !!lookup?.entry} data-testid="register-add-submit" className="btn-maroon flex-1 !py-2 !text-xs inline-flex items-center justify-center gap-1.5">
            {busy ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />} Save Invoice
          </button>
        </div>
      </form>
    </Modal>
  );
}

function EntryDetail({ entry, onClose, onChange, onOpenSubmission }) {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState(entry.note || "");
  const [date, setDate] = useState(entry.expected_date || "");
  const patch = async (body, okMsg) => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/admin/register/${entry.id}`, body);
      onChange(data);
      if (okMsg) toast.success(okMsg);
    } catch (err) { toast.error(formatError(err)); } finally { setBusy(false); }
  };
  return (
    <Modal title={`Invoice ${entry.invoice_number}`} overline={`${entry.group_label} · Preparation`} onClose={onClose} testId="register-detail-modal">
      <div className="mt-4 space-y-3 text-sm">
        <div className="grid grid-cols-2 gap-3 text-xs">
          <div><p className="overline text-[10px]">Customer</p><p className="text-ink mt-0.5">{entry.customer_name || "—"}</p></div>
          <div><p className="overline text-[10px]">Website Form</p>
            {entry.form_submitted ? (
              <button type="button" data-testid="detail-open-submission" onClick={() => onOpenSubmission(entry)} className="mt-0.5 inline-flex items-center gap-1 text-[#2D4A22] underline">Form Submitted <ExternalLink size={11} /></button>
            ) : <p className="text-[#8A6210] mt-0.5">Form Not Submitted</p>}
          </div>
        </div>
        <Field label="Preparation Status">
          <select data-testid="detail-prep-status" className="luxury-input mt-1 w-full" value={entry.prep_status} disabled={busy} onChange={(e) => patch({ prep_status: e.target.value }, `Preparation status set to ${e.target.value}.`)}>
            {PREP_STATUSES.map((s) => <option key={s}>{s}</option>)}
          </select>
        </Field>
        <Field label="Assigned Salesman"><SalesmanSelect value={entry.salesman || ""} onChange={(v) => patch({ salesman: v }, "Salesman updated.")} testId="detail-salesman" /></Field>
        <Field label="Expected Collection Date">
          <div className="flex gap-2 mt-1">
            <input data-testid="detail-date" type="date" className="luxury-input flex-1" value={date} onChange={(e) => setDate(e.target.value)} />
            <button type="button" disabled={busy || date === (entry.expected_date || "")} onClick={() => patch({ expected_date: date }, "Expected date saved.")} className="btn-outline-maroon !px-3 !py-1.5 !text-xs">Save</button>
          </div>
        </Field>
        <Field label="Preparation Note">
          <textarea data-testid="detail-note" className="luxury-input mt-1 min-h-[70px]" value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} />
          <button type="button" disabled={busy || note === (entry.note || "")} onClick={() => patch({ note }, "Note saved.")} className="btn-outline-maroon mt-2 !px-3 !py-1.5 !text-xs">Save Note</button>
        </Field>
      </div>
    </Modal>
  );
}

const StatCard = ({ label, value, tone, active, onClick, testId, icon: Icon }) => (
  <button type="button" onClick={onClick} data-testid={testId}
    className={`rounded-2xl border px-3 py-2.5 text-left transition-colors ${active ? "ring-2 ring-maroon/40" : ""} ${tone}`}>
    <div className="flex items-center justify-between gap-2"><span className="text-[10px] uppercase tracking-[0.18em] opacity-80">{label}</span>{Icon && <Icon size={13} />}</div>
    <p className="font-serif text-2xl mt-0.5" data-testid={`${testId}-value`}>{value}</p>
  </button>
);

const Pill = ({ children, cls, testId }) => (
  <span data-testid={testId} className={`text-[10px] uppercase tracking-[0.14em] rounded-full border px-2 py-0.5 inline-flex items-center gap-1 ${cls}`}>{children}</span>
);

function FilterPanel({ f, setF, isAdmin, onReset, active }) {
  const S = (k) => (e) => setF((v) => ({ ...v, [k]: e.target.value }));
  const inp = "luxury-input mt-1 !py-1.5 !text-xs w-full";
  return (
    <div data-testid="register-filter-panel" className="rounded-2xl border border-line bg-white p-3 sm:p-4 mb-4 grid grid-cols-2 md:grid-cols-4 gap-3">
      {isAdmin && (
        <Field label="Group"><select data-testid="register-filter-group" className={inp} value={f.group} onChange={S("group")}><option value="all">All Groups</option><option value="A">Sider</option><option value="B">Bridal</option></select></Field>
      )}
      <Field label="Preparation"><select data-testid="register-filter-prep" className={inp} value={f.prep} onChange={S("prep")}><option value="all">All</option>{PREP_STATUSES.map((p) => <option key={p}>{p}</option>)}</select></Field>
      <Field label="Website Form"><select data-testid="register-filter-sub" className={inp} value={f.sub} onChange={S("sub")}><option value="all">All</option><option value="submitted">Form Submitted</option><option value="not_submitted">Form Not Submitted</option></select></Field>
      <Field label="Salesman"><select data-testid="register-filter-salesman" className={inp} value={f.salesman} onChange={S("salesman")}><option value="all">All</option><option value="__none__">Not Assigned</option>{SALESMEN.map((s) => <option key={s} value={s}>{s}</option>)}</select></Field>
      <Field label="Attention"><select data-testid="register-filter-flag" className={inp} value={f.flag} onChange={S("flag")}><option value="">All</option><option value="overdue">Overdue</option><option value="collection_today">Due Today</option><option value="approaching">Due Soon</option><option value="not_ready">Not Ready</option></select></Field>
      <Field label="Customer"><input data-testid="register-filter-customer" className={inp} value={f.customer} onChange={S("customer")} placeholder="Name contains…" /></Field>
      <Field label="Invoice From"><input data-testid="register-filter-inv-from" className={inp} inputMode="numeric" maxLength={4} value={f.invFrom} onChange={S("invFrom")} placeholder="e.g. 2200" /></Field>
      <Field label="Invoice To"><input data-testid="register-filter-inv-to" className={inp} inputMode="numeric" maxLength={4} value={f.invTo} onChange={S("invTo")} placeholder="e.g. 2299" /></Field>
      <Field label="Expected From"><input data-testid="register-filter-from" type="date" className={inp} value={f.dateFrom} onChange={S("dateFrom")} /></Field>
      <Field label="Expected To"><input data-testid="register-filter-to" type="date" className={inp} value={f.dateTo} onChange={S("dateTo")} /></Field>
      <div className="col-span-2 md:col-span-4 flex justify-end">
        <button type="button" disabled={!active} data-testid="register-filter-clear" onClick={onReset} className="btn-outline-maroon !px-3 !py-1.5 !text-xs disabled:opacity-40">Reset Filters</button>
      </div>
    </div>
  );
}

const GapPanel = ({ gaps, onAdd }) => {
  const [open, setOpen] = useState(false);
  const groups = Object.entries(gaps || {}).filter(([, v]) => v.count > 0);
  const total = groups.reduce((n, [, v]) => n + v.count, 0);
  if (!total) return null;
  return (
    <div data-testid="register-gaps" className="rounded-2xl border border-[#E3C98B] bg-[#FFFBF2] px-4 py-3 mb-4">
      <button type="button" data-testid="register-gaps-toggle" onClick={() => setOpen((v) => !v)} className="w-full flex items-center justify-between gap-3 text-left">
        <div>
          <p className="overline text-[10px] text-[#8A6210]">Not in Register</p>
          <p className="text-xs text-muted2 mt-0.5">
            <span className="text-ink font-medium" data-testid="register-gaps-count">{total}</span> invoice number{total === 1 ? "" : "s"} between your lowest and highest known invoice have no register entry, website form or pre-registration. Tap a number to add it.
          </p>
        </div>
        <span className="text-[10px] uppercase tracking-[0.16em] text-[#8A6210] whitespace-nowrap">{open ? "Hide" : "Show"}</span>
      </button>
      {open && groups.map(([g, v]) => (
        <div key={g} className="mt-3" data-testid={`register-gaps-${g}`}>
          <p className="text-[10px] uppercase tracking-[0.18em] text-muted2 mb-1.5">{g === "A" ? "Sider" : "Bridal"} · {v.lo}–{v.hi} · {v.count} missing</p>
          <div className="flex flex-wrap gap-1.5 max-h-40 overflow-y-auto">
            {v.numbers.map((n) => (
              <button key={n} type="button" data-testid={`register-gap-${n}`} onClick={() => onAdd({ invoice_number: n, group: g })}
                className="rounded-full border border-[#E3C98B] bg-white px-2.5 py-1 text-xs text-[#8A6210] hover:bg-maroon hover:text-ivory hover:border-maroon transition-colors">#{n}</button>
            ))}
            {v.count > v.numbers.length && <span className="text-xs text-muted2 self-center">+{v.count - v.numbers.length} more</span>}
          </div>
        </div>
      ))}
    </div>
  );
};

export default function PreparationRegister({ me, onOpenSubmission }) {
  const isAdmin = me?.role === "admin";
  const fixedGroup = me?.role === "staff_group_b" ? "B" : me?.role === "staff_group_a" ? "A" : null;
  const [data, setData] = useState({ entries: [], summary: {} });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [f, setF] = useState(EMPTY_FILTERS);
  const [sort, setSort] = useState("invoice_desc");
  const [lifeView, setLifeView] = useState("active");
  const [showFilters, setShowFilters] = useState(false);
  const [modal, setModal] = useState(null);
  const [preset, setPreset] = useState(null);
  const [detail, setDetail] = useState(null);

  useEffect(() => { const t = setTimeout(() => setDebouncedQ(q.trim()), 250); return () => clearTimeout(t); }, [q]);

  const activeFilterCount = Object.keys(EMPTY_FILTERS).filter((k) => f[k] !== EMPTY_FILTERS[k] && !(k === "group" && !isAdmin)).length;

  const load = async () => {
    setLoading(true);
    try {
      const params = { view: lifeView, sort };
      if (isAdmin && f.group !== "all") params.group = f.group;
      if (f.prep !== "all") params.prep_status = f.prep;
      if (f.sub !== "all") params.submission = f.sub;
      if (f.salesman !== "all") params.salesman = f.salesman;
      if (f.customer.trim()) params.customer = f.customer.trim();
      if (f.dateFrom) params.date_from = f.dateFrom;
      if (f.dateTo) params.date_to = f.dateTo;
      if (/^\d{1,4}$/.test(f.invFrom)) params.inv_from = f.invFrom;
      if (/^\d{1,4}$/.test(f.invTo)) params.inv_to = f.invTo;
      if (f.flag) params.flag = f.flag;
      if (debouncedQ) params.q = debouncedQ;
      const { data: d } = await api.get("/admin/register", { params });
      setData(d);
    } catch (e) { toast.error(formatError(e)); } finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [f, debouncedQ, sort, lifeView]);

  const s = data.summary || {};
  const rows = data.entries;
  const upsert = () => load();
  const toggleFlag = (flag) => setF((v) => ({ ...v, flag: v.flag === flag ? "" : flag }));
  const reset = () => { setF(EMPTY_FILTERS); setQ(""); };

  return (
    <div data-testid="preparation-register">
      <div className="rounded-2xl border border-line bg-[#FAF7F2] px-4 py-3 mb-4 flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex-1">
          <p className="overline text-[10px]">Internal · Bill Book tracker</p>
          <p className="text-xs text-muted2 mt-1 leading-relaxed">Every Bill Book invoice in one place — website submissions and pre-registered invoices link here automatically; add only the ones that are missing.</p>
        </div>
        <button type="button" data-testid="register-add-btn" onClick={() => { setPreset(null); setModal("add"); }} className="btn-maroon !px-4 !py-2 !text-xs inline-flex items-center gap-1.5 justify-center"><Plus size={13} /> Add Invoice</button>
      </div>

      <GapPanel gaps={data.not_in_register} onAdd={(p) => { setPreset(p); setModal("add"); }} />

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 mb-4" data-testid="register-stats">
        <StatCard label="Form Not Submitted" value={s.form_not_submitted ?? 0} icon={ClipboardList} tone="bg-[#FBEFD5] text-[#8A6210] border-[#E3C98B]" active={f.sub === "not_submitted"} onClick={() => setF((v) => ({ ...v, sub: v.sub === "not_submitted" ? "all" : "not_submitted" }))} testId="stat-not-submitted" />
        <StatCard label="Collection Today" value={s.collection_today ?? 0} icon={CalendarClock} tone="bg-[#E3EEF8] text-[#1F4E79] border-[#1F4E79]/25" active={f.flag === "collection_today"} onClick={() => toggleFlag("collection_today")} testId="stat-today" />
        <StatCard label="Overdue" value={s.overdue ?? 0} icon={AlertTriangle} tone="bg-[#F5D3D3] text-[#8A2A2A] border-[#8A2A2A]/25" active={f.flag === "overdue"} onClick={() => toggleFlag("overdue")} testId="stat-overdue" />
        <StatCard label="Due Soon" value={s.approaching ?? 0} icon={CalendarClock} tone="bg-[#FBEFD5] text-[#8A6210] border-[#E3C98B]" active={f.flag === "approaching"} onClick={() => toggleFlag("approaching")} testId="stat-approaching" />
        <StatCard label="Not Ready" value={s.not_ready ?? 0} icon={RefreshCw} tone="bg-[#F1ECE4] text-[#6B5B4A] border-[#D9CDB8]" active={f.flag === "not_ready"} onClick={() => toggleFlag("not_ready")} testId="stat-not-ready" />
      </div>

      <div className="flex flex-wrap gap-2 mb-3 items-center" data-testid="register-toolbar">
        <div className="relative flex-1 min-w-[180px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted2" />
          <input data-testid="register-search" className="luxury-input !pl-9 !py-2 !text-xs w-full" placeholder="Search invoice, customer, salesman, note…" value={q} onChange={(e) => setQ(e.target.value)} />
          {q && <button type="button" data-testid="register-search-clear" onClick={() => setQ("")} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted2 hover:text-maroon"><X size={13} /></button>}
        </div>
        <button type="button" data-testid="register-filter-toggle" onClick={() => setShowFilters((v) => !v)}
          className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-2 text-xs transition-colors ${showFilters || activeFilterCount ? "bg-maroon text-ivory border-maroon" : "bg-white text-muted2 border-line hover:text-maroon"}`}>
          <SlidersHorizontal size={13} /> Filters{activeFilterCount ? <span data-testid="register-filter-count" className="ml-0.5 rounded-full bg-white/20 px-1.5 text-[10px]">{activeFilterCount}</span> : null}
        </button>
        <select data-testid="register-sort" className="luxury-input !w-auto !py-2 !text-xs" value={sort} onChange={(e) => setSort(e.target.value)}>
          {SORTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <div className="inline-flex rounded-full border border-line bg-white p-0.5" data-testid="register-lifecycle-toggle">
          {[["active", "Active"], ["delivered", "Delivered"], ["deleted", "Recycle Bin"]].map(([v, l]) => (
            <button key={v} type="button" data-testid={`register-view-${v}`} onClick={() => setLifeView(v)}
              className={`px-3 py-1 text-[10px] uppercase tracking-[0.16em] rounded-full transition-colors ${lifeView === v ? "bg-maroon text-ivory" : "text-muted2 hover:text-maroon"}`}>{l}</button>
          ))}
        </div>
        <button type="button" onClick={async () => { try { const { data: r } = await api.post("/admin/register/sync"); toast.success(`Sync complete — ${r.created} new entr${r.created === 1 ? "y" : "ies"} created, ${r.linked} already linked.`); load(); } catch (e) { toast.error(formatError(e)); } }} className="text-xs text-maroon underline" data-testid="register-sync">Sync submissions</button>
        <button type="button" onClick={load} className="text-muted2 hover:text-maroon" title="Refresh" data-testid="register-refresh"><RefreshCw size={14} className={loading ? "animate-spin" : ""} /></button>
      </div>

      {showFilters && <FilterPanel f={f} setF={setF} isAdmin={isAdmin} onReset={reset} active={activeFilterCount > 0 || !!q} />}

      {(activeFilterCount > 0 || debouncedQ) && (
        <p className="text-xs text-muted2 mb-3" data-testid="register-active-filters">
          Showing {rows.length} result{rows.length === 1 ? "" : "s"}{debouncedQ ? ` for “${debouncedQ}”` : ""}{activeFilterCount ? ` · ${activeFilterCount} filter${activeFilterCount === 1 ? "" : "s"} active` : ""} —{" "}
          <button type="button" onClick={reset} className="text-maroon underline" data-testid="register-reset-inline">clear all</button>
        </p>
      )}

      {loading && !rows.length ? (
        <p className="text-sm text-muted2 py-10 text-center">Loading register…</p>
      ) : rows.length === 0 ? (
        <div className="text-center py-12 border border-dashed border-line rounded-2xl" data-testid="register-empty">
          <ClipboardList size={28} className="mx-auto text-muted2" strokeWidth={1.4} />
          <p className="text-sm text-muted2 mt-3">No register entries match. Use “Add Invoice” to track a Bill Book invoice that has no website submission yet.</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="register-list">
          {rows.map((e) => (
            <button type="button" key={e.id} data-testid={`register-row-${e.invoice_number}`} onClick={() => setDetail(e)}
              className={`w-full text-left rounded-2xl border bg-white px-4 py-3 hover:border-maroon/40 transition-colors ${e.flags?.overdue ? "border-[#8A2A2A]/25" : "border-line"}`}>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
                <span className="font-serif text-lg text-ink">#{e.invoice_number}</span>
                <span className="text-[10px] uppercase tracking-[0.18em] text-muted2">{e.group_label}</span>
                {e.lifecycle === "delivered" && <Pill testId={`row-delivered-${e.invoice_number}`} cls="bg-[#E5E7EB] text-[#374151] border-[#9CA3AF]/40">Delivered</Pill>}
                {e.lifecycle === "deleted" && <Pill testId={`row-deleted-${e.invoice_number}`} cls="bg-[#E5E7EB] text-[#374151] border-[#9CA3AF]/40">In Recycle Bin</Pill>}
                {e.pre_registered && <Pill testId={`row-prereg-${e.invoice_number}`} cls="bg-[#F1ECE4] text-[#6B5B4A] border-[#D9CDB8]">Pre-Registered</Pill>}
                <Pill testId={`row-prep-${e.invoice_number}`} cls={PREP_CLS[e.prep_status]}>{e.prep_status}</Pill>
                <Pill testId={`row-form-${e.invoice_number}`} cls={e.form_submitted ? "bg-[#D6E9D5] text-[#2D4A22] border-[#2D4A22]/25" : "bg-[#FBEFD5] text-[#8A6210] border-[#E3C98B]"}>
                  {e.form_submitted ? <CheckCircle2 size={10} /> : <ClipboardList size={10} />}{e.form_submitted ? "Form Submitted" : "Form Not Submitted"}
                </Pill>
                {e.needs_review && <Pill testId={`row-review-${e.invoice_number}`} cls="bg-[#F5D3D3] text-[#8A2A2A] border-[#8A2A2A]/25"><AlertTriangle size={10} /> Needs Review</Pill>}
                {e.flags?.overdue && <Pill testId={`row-overdue-${e.invoice_number}`} cls="bg-[#F5D3D3] text-[#8A2A2A] border-[#8A2A2A]/25">Past Expected Date</Pill>}
                {e.flags?.collection_today && <Pill cls="bg-[#E3EEF8] text-[#1F4E79] border-[#1F4E79]/25">Collection Today</Pill>}
                {e.flags?.approaching && <Pill cls="bg-[#FBEFD5] text-[#8A6210] border-[#E3C98B]">Due Soon</Pill>}
              </div>
              <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-muted2">
                <span>Expected: <span className="text-ink">{fmtDate(e.expected_date)}</span></span>
                <span>Salesman: <span className="text-ink">{e.salesman || "Not Assigned"}</span></span>
                {e.customer_name && <span>Customer: <span className="text-ink">{e.customer_name}</span></span>}
              </div>
            </button>
          ))}
        </div>
      )}

      {modal === "add" && <AddInvoiceModal fixedGroup={fixedGroup} preset={preset} onClose={() => setModal(null)} onSaved={() => { setModal(null); upsert(); }} />}
      {detail && <EntryDetail entry={data.entries.find((e) => e.id === detail.id) || detail} onClose={() => setDetail(null)} onChange={upsert} onOpenSubmission={(e) => { setDetail(null); onOpenSubmission(e); }} />}
    </div>
  );
}
